import { useEffect, useState } from 'react'
import { getFunctions, httpsCallable } from 'firebase/functions'
import {
  addDoc,
  collection,
  doc,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  where,
} from 'firebase/firestore'
import { app, db } from './firebase'
import { useAuth } from './auth'
import type { Artist, ArtistKind, BandMember, PartyMember, Person, WithId } from '../types/models'

/* ============================================================================
   Organisations and artists.

   A tour is temporary; an artist is permanent. Every account gets an
   organisation and one artist automatically, named after them — a solo user
   never sees either, and the day they add a second artist is a normal Tuesday
   rather than a migration.
   ============================================================================ */

const provision = httpsCallable<Record<string, never>, {
  orgId: string
  artistId: string
  created: boolean
  adopted?: number
}>(getFunctions(app), 'provisionAccount')

/**
 * Ensures this account has an organisation and a first artist, then tracks the
 * roster.
 *
 * Provisioning is idempotent and runs on every sign-in. Failure is quiet on
 * purpose: it is invisible to a solo user, and an error about an
 * "organisation" they never asked for is worse than the missing record it
 * reports. The next load tries again.
 */
export function useArtists() {
  const { user } = useAuth()
  const [orgId, setOrgId] = useState<string | null>(null)
  const [artists, setArtists] = useState<Array<WithId<Artist>>>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!user) {
      setOrgId(null)
      setArtists([])
      setLoading(false)
      return
    }
    let alive = true
    provision({})
      .then((result) => alive && setOrgId(result.data.orgId))
      .catch((error) => console.warn('[artists] provisioning failed', error))
      .finally(() => alive && setLoading(false))
    return () => {
      alive = false
    }
  }, [user])

  useEffect(() => {
    if (!orgId) return
    return onSnapshot(
      query(collection(db, 'artists'), where('orgId', '==', orgId)),
      (snapshot) => {
        setArtists(
          snapshot.docs
            .map((entry) => ({ id: entry.id, ...entry.data() }) as WithId<Artist>)
            .filter((artist) => !artist.archived)
            .sort((a, b) => a.name.localeCompare(b.name)),
        )
      },
      (error) => console.warn('[artists] roster', error),
    )
  }, [orgId])

  return {
    orgId,
    /** Everything, including the implicit artist. Use this when a tour needs
     *  something to belong to. */
    artists,
    /**
     * What a person should be shown.
     *
     * Excludes the artist `provisionAccount` makes on every account. A solo
     * user never sees it either way; on an agency roster it is the account
     * holder's own name sitting among their clients. Editing it clears the
     * flag, so an act that genuinely is the account holder stays.
     */
    roster: artists.filter((artist) => !artist.autoCreated),
    loading,
    /** True once there is more than one act to choose between. Counts real
     *  signings only — the implicit artist is not a second act. */
    isRoster: artists.filter((artist) => !artist.autoCreated).length > 1,
  }
}

/* ------------------------------------------------------------- one artist */

export function useArtist(artistId: string | undefined) {
  const [artist, setArtist] = useState<WithId<Artist> | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!artistId) {
      setArtist(null)
      setLoading(false)
      return
    }
    return onSnapshot(
      doc(db, 'artists', artistId),
      (snapshot) => {
        setArtist(
          snapshot.exists() ? ({ id: snapshot.id, ...snapshot.data() } as WithId<Artist>) : null,
        )
        setLoading(false)
      },
      () => setLoading(false),
    )
  }, [artistId])

  return { artist, loading }
}

/** Everyone in the organisation who travels. Org-level, so shared by every act. */
export function usePeople(orgId: string | null) {
  const [people, setPeople] = useState<Array<WithId<Person>>>([])

  useEffect(() => {
    if (!orgId) return
    return onSnapshot(
      query(collection(db, 'people'), where('orgId', '==', orgId)),
      (snapshot) => {
        setPeople(
          snapshot.docs
            .map((entry) => ({ id: entry.id, ...entry.data() }) as WithId<Person>)
            .sort((a, b) => a.name.localeCompare(b.name)),
        )
      },
      (error) => console.warn('[people]', error),
    )
  }, [orgId])

  return people
}

/* --------------------------------------------------------------- mutations */

export interface ArtistDraft {
  name: string
  kind?: ArtistKind
  avatarURL?: string | null
}

export async function createArtist(draft: ArtistDraft, orgId: string): Promise<string> {
  const reference = await addDoc(collection(db, 'artists'), {
    orgId,
    name: draft.name.trim(),
    kind: draft.kind ?? 'solo',
    members: [],
    avatarURL: draft.avatarURL?.trim() || null,
    archived: false,
    defaultTouringParty: [],
    createdAt: serverTimestamp(),
  })
  return reference.id
}

export async function updateArtist(artistId: string, draft: ArtistDraft): Promise<void> {
  await setDoc(
    doc(db, 'artists', artistId),
    {
      name: draft.name.trim(),
      kind: draft.kind ?? 'solo',
      avatarURL: draft.avatarURL?.trim() || null,
      // Editing it makes it real. The implicit artist is hidden from a roster
      // because nobody asked for it; the moment somebody does ask for it, by
      // naming it, it stops being implicit.
      autoCreated: false,
    },
    { merge: true },
  )
}

/**
 * Archives rather than deletes.
 *
 * Tours point at an artist. Deleting one would leave every past run pointing
 * at nothing — losing the tour's history, not the artist's. The rules refuse
 * the delete outright, so this is the only way out.
 */
export async function archiveArtist(artistId: string, archived = true): Promise<void> {
  await setDoc(doc(db, 'artists', artistId), { archived }, { merge: true })
}

/** Who is in the band. A solo act keeps an empty list rather than no field. */
export async function setBandMembers(
  artistId: string,
  members: BandMember[],
): Promise<void> {
  await setDoc(doc(db, 'artists', artistId), { members }, { merge: true })
}

export async function setTouringParty(
  artistId: string,
  party: PartyMember[],
): Promise<void> {
  await setDoc(doc(db, 'artists', artistId), { defaultTouringParty: party }, { merge: true })
}

/* ------------------------------------------------------------------ people */

export interface PersonDraft {
  name: string
  email?: string
  phone?: string
  nationality?: string
  dietaryRequirements?: string
  allergies?: string
  roomPreference?: string
  seatPreference?: string
}

export async function createPerson(draft: PersonDraft, orgId: string): Promise<string> {
  const reference = await addDoc(collection(db, 'people'), {
    orgId,
    ...clean(draft),
    createdAt: serverTimestamp(),
  })
  return reference.id
}

export async function updatePerson(personId: string, draft: PersonDraft): Promise<void> {
  await setDoc(doc(db, 'people', personId), clean(draft), { merge: true })
}

/** Trims, and drops empty strings rather than writing them. */
function clean(draft: PersonDraft): Record<string, string> {
  const out: Record<string, string> = {}
  for (const [key, value] of Object.entries(draft)) {
    const trimmed = (value ?? '').trim()
    if (trimmed) out[key] = trimmed
  }
  return out
}
